import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    ADVANCED_OPERATORS,
    AdvancedMatch,
    AdvancedOperator,
    AdvancedSearchField,
    AdvancedSearchRule,
    AdvancedSearchState,
    activeRules,
    cloneAdvancedState,
    createRule,
    describeRule,
    emptyAdvancedState,
    getOperator
} from '../../utils/advancedSearch';

/**
 * Ricerca avanzata espandibile: di default è una barra sottile con il riepilogo delle condizioni
 * attive; al clic si apre e permette di combinare più campi in AND oppure in OR.
 */
@Component({
    selector: 'app-advanced-search',
    templateUrl: './advanced-search.component.html',
    styleUrls: ['./advanced-search.component.css']
})
export class AdvancedSearchComponent {
    @Input() fields: AdvancedSearchField[] = [];
    @Input() state: AdvancedSearchState = emptyAdvancedState();
    @Output() stateChange = new EventEmitter<AdvancedSearchState>();

    expanded = false;

    get activeCount(): number {
        return activeRules(this.state, this.fields).length;
    }

    get summary(): string[] {
        return activeRules(this.state, this.fields).map(rule => describeRule(rule, this.fields));
    }

    get joinLabel(): string {
        return this.state.match === 'or' ? 'O' : 'E';
    }

    toggle(): void {
        this.expanded = !this.expanded;
        if (this.expanded && this.state.rules.length === 0) {
            this.addRule();
        }
    }

    trackById(_: number, rule: AdvancedSearchRule): number {
        return rule.id;
    }

    fieldOf(rule: AdvancedSearchRule): AdvancedSearchField | undefined {
        return this.fields.find(field => field.key === rule.field);
    }

    operatorsOf(rule: AdvancedSearchRule): AdvancedOperator[] {
        const field = this.fieldOf(rule);
        return field ? ADVANCED_OPERATORS[field.type] : [];
    }

    inputsOf(rule: AdvancedSearchRule): number {
        return getOperator(this.fieldOf(rule), rule.operator)?.inputs ?? 1;
    }

    addRule(): void {
        const next = cloneAdvancedState(this.state);
        next.rules.push(createRule(this.fields[0]));
        this.expanded = true;
        this.emit(next);
    }

    removeRule(rule: AdvancedSearchRule): void {
        const next = cloneAdvancedState(this.state);
        next.rules = next.rules.filter(candidate => candidate.id !== rule.id);
        this.emit(next);
    }

    setMatch(match: AdvancedMatch): void {
        const next = cloneAdvancedState(this.state);
        next.match = match;
        this.emit(next);
    }

    reset(): void {
        this.emit(emptyAdvancedState());
    }

    onFieldChange(rule: AdvancedSearchRule, key: string): void {
        const field = this.fields.find(candidate => candidate.key === key);
        this.update(rule, target => {
            target.field = key;
            // Cambiando tipo di campo gli operatori e i valori precedenti non hanno più senso.
            target.operator = ADVANCED_OPERATORS[field?.type ?? 'text'][0].value;
            target.value = '';
            target.valueTo = '';
        });
    }

    onOperatorChange(rule: AdvancedSearchRule, operator: string): void {
        this.update(rule, target => {
            target.operator = operator;
            if (getOperator(this.fieldOf(rule), operator)?.inputs === 0) {
                target.value = '';
                target.valueTo = '';
            }
        });
    }

    onValueChange(rule: AdvancedSearchRule, value: string): void {
        this.update(rule, target => target.value = value ?? '');
    }

    onValueToChange(rule: AdvancedSearchRule, value: string): void {
        this.update(rule, target => target.valueTo = value ?? '');
    }

    private update(rule: AdvancedSearchRule, mutate: (target: AdvancedSearchRule) => void): void {
        const next = cloneAdvancedState(this.state);
        const target = next.rules.find(candidate => candidate.id === rule.id);
        if (!target) {
            return;
        }
        mutate(target);
        this.emit(next);
    }

    private emit(next: AdvancedSearchState): void {
        this.state = next;
        this.stateChange.emit(next);
    }
}
